import { apiGet, APIURL, cancelSelectEvent, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from "./../../helpers";
import {
  WidgetConfig,
  WidgetEvent,
  WidgetEventHandler,
  WidgetEventType,
} from "./../../types";

const DEFAULT_SECTORS = [
  "Healthcare",
  "Energy",
  "Basic Materials",
  "Technology",
  "Communication Services",
  "Consumer Cyclical",
  "Consumer Defensive",
  "Financial Services",
  "Industrials",
  "Real Estate",
  "Utilities",
];

export interface ValuationGaugeDashboardConfig extends WidgetConfig {
  mover_type?: string;
  interval?: string;
  exchange?: string;
  sectors?: string[];
  benchmark_type?: string;
  custom_benchmark?: number;
}

export interface ValuationGaugeData {
  sector: string;
  exchange: string;
  pe: number;
  benchmark_pe: number;
  relative_position: number;
  date: string;
}

export class TraderverseValuationGaugeDashboard {
  private container: HTMLElement;
  private config: ValuationGaugeDashboardConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private shadowRoot: ShadowRoot;
  private widgetResizeObserver: ResizeObserver | null = null;
  private currentChart: any = [];
  private isChartInitialized: boolean = false;
  private isLoading: boolean = true;

  constructor(config: ValuationGaugeDashboardConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: "closed" });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.handleResize();
    });

    setTimeout(() => {
      const widget = this.shadowRoot.querySelector(".widgetBox") as HTMLElement;
      if (widget) {
        this.widgetResizeObserver?.observe(widget);
      }
    }, 100);
  }

  private getDefaultConfig(): Partial<ValuationGaugeDashboardConfig> {
    return {
      headerPinned: true,
      theme: "default-dark",
      width: "100%",
      height: "400px",
      autosize: true,
      exchange: "NASDAQ",
      sectors: [...DEFAULT_SECTORS],
      benchmark_type: "market_average",
      custom_benchmark: 25,
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === "string") {
      const el =
        document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    this.isLoading = true;

    this.createWidget();
    this.setupEventListeners();

    this.attachStyles();
    const chartLibLoaded = await this.injectChartLibrary();
    if (chartLibLoaded) {
      await this.setupChart();
      this.isChartInitialized = true;
    }

    this.isLoading = false;
    const widget = this.shadowRoot.querySelector(".widgetBox");
    if (widget) {
      widget.classList.remove("loading");
    }
    this.emit("ready", {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = "defaultDarkMode";
    let dataTheme = "default-dark";

    if (this.config.theme === "white") {
      themeClass = "whiteMode";
      dataTheme = "white";
    } else if (this.config.theme === "black") {
      themeClass = "blackMode";
      dataTheme = "black";
    } else if (this.config.theme === "default-lite") {
  themeClass = "defaultLightMode";
  dataTheme = "default-lite";
}

    const selectedSectors =
      Array.isArray(this.config.sectors) && this.config.sectors.length > 0
        ? this.config.sectors
        : DEFAULT_SECTORS;
    const sectorChkOptions = DEFAULT_SECTORS.map((sector) => {
      const isChecked = selectedSectors.includes(sector) ? "checked" : "";
      return `<div class="checkBoxField"> <input class="filter-checkbox sector-filter-checkbox" value="${sector}" type="checkbox" ${isChecked} /> <label>${sector} <i>✓</i></label> </div>`;
    }).join("");

    const widgetHtml = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox traderverse-widget ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme} ${this.isLoading ? "loading" : ""}" data-theme="${this.config.theme}">
        <div class="headTop">
          <h4 class="title market">${this.getTitleIconSvg()} Valuation Gauge Dashboard </h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
            <div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div>
          </div>
        </div>
        <div class="inBody">
          <div class="optionsRow">
            <div class="item">
              <select class="exchange-dropdown">
                <option value="NASDAQ">NASDAQ</option>
                <option value="NYSE">NYSE</option>
                <option value="AMEX">AMEX</option>
              </select>
            </div>
            <span></span>
            <div class="filtersBox sideBar hidden">
              <div class="item checkBoxes">
                ${sectorChkOptions}
              </div>
              <div class="item">
                <select class="benchmark-type-dropdown">
                  <option value="market_average">Market Average</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div class="item">
                <input type="number" class="custom-benchmark-input" min="0" max="100" value="25" />
              </div>

            </div>
          </div>
          <div class="chartDv">
            <div class="gaudeDashDv">
            </div>
          </div>
        </div>
      </div>
    `;
    this.shadowRoot.innerHTML = widgetHtml;
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
      // const tagStyle = document.createElement('style');
      // tagStyle.textContent = `
      //   .sector-search-container {
      //     display: flex;
      //     flex-wrap: wrap;
      //     align-items: center;
      //     gap: 4px;
      //   }
      //   .sector-tag {
      //     background: #1976d2;
      //     color: #fff;
      //     border-radius: 12px;
      //     padding: 2px 10px 2px 8px;
      //     margin: 2px 2px 2px 0;
      //     display: inline-flex;
      //     align-items: center;
      //     font-size: 13px;
      //   }
      //   .sector-tag button.remove-sector {
      //     background: none;
      //     border: none;
      //     color: #fff;
      //     margin-left: 4px;
      //     cursor: pointer;
      //     font-size: 15px;
      //     line-height: 1;
      //   }
      //   .sector-search-input {
      //     border: 1px solid #ccc;
      //     border-radius: 8px;
      //     padding: 2px 8px;
      //     font-size: 13px;
      //     min-width: 120px;
      //     outline: none;
      //   }
      // `;
      // this.shadowRoot.appendChild(tagStyle);
    } catch (err) {
      console.error("Failed to load CSS file:", err);
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const exchangeDropdown = this.shadowRoot.querySelector(
      ".exchange-dropdown",
    ) as HTMLSelectElement;
    if (exchangeDropdown) {
      exchangeDropdown.value = this.config.exchange || "NASDAQ";
      exchangeDropdown.addEventListener("change", async (e) => {
        this.config.exchange = (e.target as HTMLSelectElement).value;
        await this.setupChart();
      });
    }

    const benchmarkTypeDropdown = this.shadowRoot.querySelector(
      ".benchmark-type-dropdown",
    ) as HTMLSelectElement;
    if (benchmarkTypeDropdown) {
      benchmarkTypeDropdown.value =
        this.config.benchmark_type || "market_average";
      benchmarkTypeDropdown.addEventListener("change", async (e) => {
        this.config.benchmark_type = (e.target as HTMLSelectElement).value;
        await this.setupChart();
      });
    }

    const customBenchmarkInput = this.shadowRoot.querySelector(
      ".custom-benchmark-input",
    ) as HTMLInputElement;
    if (customBenchmarkInput) {
      customBenchmarkInput.value = (
        this.config.custom_benchmark || 25
      ).toString();
      customBenchmarkInput.addEventListener("change", async (e) => {
        this.config.custom_benchmark = parseFloat(
          (e.target as HTMLInputElement).value,
        );
        await this.setupChart();
      });
    }
    const filterBtn = this.shadowRoot.querySelector(
      ".filters-toggle",
    ) as HTMLButtonElement;
    const filtersBox = this.shadowRoot.querySelector(
      ".filtersBox",
    ) as HTMLElement;
    if (filterBtn && filtersBox) {
      filterBtn.addEventListener("click", () => {
        filtersBox.classList.toggle("hidden");
      });
    }

    const sectorCheckboxes = this.shadowRoot.querySelectorAll(
      ".sector-filter-checkbox",
    );
    sectorCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", async () => {
        const selected: string[] = [];
        this.shadowRoot
          .querySelectorAll(".sector-filter-checkbox")
          .forEach((cb) => {
            const input = cb as HTMLInputElement;
            if (input.checked) selected.push(input.value);
          });
        this.config.sectors = selected;
        await this.setupChart();
      });
    });
  }

  private async fetchValuationGaugeData(): Promise<any> {
    try {
      const sectors =
        this.config.sectors?.length ? this.config.sectors : DEFAULT_SECTORS;
      const params = [
        `p_exchange=${encodeURIComponent(this.config.exchange || "NASDAQ")}`,
        `p_sectors=${sectors.map((s) => encodeURIComponent(s)).join(",")}`,
        `p_benchmark_type=${encodeURIComponent(this.config.benchmark_type || "market_average")}`,
        `p_custom_benchmark=${this.config.custom_benchmark ?? 25}`,
      ].join("&");

      const url = `${APIURL}/v1/discover/valuation-gauge?${params}`;

      const response = await apiGet({
        method: "GET",
        url,
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      console.error("Error fetching valuation gauge data:", err);
      return null;
    }
  }

  private async drawChart(gaugeData: any[]): Promise<void> {
    const chartDv = this.shadowRoot.querySelector(".chartDv") as HTMLElement;
    try {
      const echarts = (window as any).echarts;
      if (!echarts) {
        console.error("ECharts not available");
        return;
      }

      const gaugeDashDiv = this.shadowRoot.querySelector(".gaudeDashDv");
      if (!gaugeDashDiv) {
        console.error("Gauge container not found");
        return;
      }
      gaugeDashDiv.innerHTML = "";
      this.currentChart = [];
chartDv?.setAttribute(
  "data-testing-id",
  "valuation-gauge-dashboard"
);
      if (!gaugeData || gaugeData.length === 0) {
       chartDv?.removeAttribute("data-testing-id");

        gaugeDashDiv.innerHTML = getNoDataHTML();
        return;
      }

      let options: any[] = [];

      for (let i = 0; i < gaugeData.length; i++) {
        const data = gaugeData[i];
        const itemDiv = document.createElement("div");
        itemDiv.className = "item";
        const chartDiv = document.createElement("div");
        chartDiv.className = "gauge-chart";
        chartDiv.id = `gauge-chart-${i}`;
        const valueP = document.createElement("p");
        valueP.textContent = Math.round(data.value).toString();
        const titleH4 = document.createElement("h4");
        titleH4.textContent = data.sector;

        itemDiv.appendChild(chartDiv);
        itemDiv.appendChild(valueP);
        itemDiv.appendChild(titleH4);
        gaugeDashDiv.appendChild(itemDiv);
        if (this.currentChart[i]) {
          this.currentChart[i].dispose();
          this.currentChart[i] = null;
          options[i] = {};
        }
        options[i] = {
          series: [
            {
              type: "gauge",
              startAngle: 180,
              endAngle: 0,
              min: 0,
              max: 100,
              splitNumber: 5,
              center: ["50%", "55%"],
              radius: "100%",
              axisLine: {
                lineStyle: {
                  width: 20,
                  color: [
                    [0.33, "#009E3A"], // green
                    [0.66, "#FFB21D"], // orange
                    [1, "#FF3B3B"], // red
                  ],
                },
              },
              axisTick: { show: false },
              splitLine: { show: false },
              axisLabel: { show: false },
              detail: {
                show: false,
                formatter: "{value}%",
                fontSize: 16,
                fontWeight: "bold",
                color: this.config.theme === "white" ? "#333" : "#fff",
              },
              data: [{ value: data.value, name: "Valuation" }],
              title: {
                show: false,
              },
              pointer: {
                icon: "path://M2,0 L-2,0 L0,-80 Z",
                length: "80%",
                width: 6,
                offsetCenter: [0, "0%"],
                itemStyle: {
                  color: this.config.theme === "white" ? "#333" : "#fff",
                },
              },
            },
          ],
        };
      }
      setTimeout(() => {
        // Now draw charts after all promises resolve
        for (let i = 0; i < gaugeData.length; i++) {
          if (options[i]) {
            const chartDiv = this.shadowRoot.getElementById(`gauge-chart-${i}`);
            this.currentChart[i] = echarts.init(chartDiv);
            this.currentChart[i].setOption(options[i]);
          }
        }
      });
      window.addEventListener("resize", () => {
        for (let i = 0; i < gaugeData.length; i++) {
          this.currentChart[i].resize();
        }
      });
    } catch (err) {
      console.error("Error drawing chart:", err);
    }
  }

  private calculateValuationScore(pe: number, benchmark_pe: number): number {
    const score = Math.max(0, Math.min(100, (1 - pe / benchmark_pe) * 100));
    return Math.round(score * 10) / 10; // Round to 1 decimal place
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) {
        console.log("ECharts already loaded globally");
        return resolve(true);
      }

      const existingScript = this.shadowRoot.querySelector(
        "script[data-chart-lib]",
      );
      if (existingScript) {
        if ((window as any).echarts) {
          resolve(true);
        } else {
          (existingScript as HTMLScriptElement).onload = () => resolve(true);
          (existingScript as HTMLScriptElement).onerror = () => resolve(false);
        }
        return;
      }

      const script = document.createElement("script");
      script.src = "https://cdn.jsdelivr.net/npm/echarts@6/dist/echarts.min.js";
      script.async = true;
      script.setAttribute("data-chart-lib", "true");

      script.onload = () => {
        console.log("ECharts loaded successfully");
        resolve(true);
      };

      script.onerror = () => {
        console.error("Failed to load ECharts");
        resolve(false);
      };

      this.shadowRoot.appendChild(script);
    });
  }

  private async setupChart(): Promise<void> {
    try {
      const apiData = await this.fetchValuationGaugeData();
      if (apiData && apiData.data && Array.isArray(apiData.data)) {
        const gaugeData = apiData.data.map((item: ValuationGaugeData) => ({
          sector: item.sector || "Unknown",
          exchange: item.exchange,
          pe: item.pe,
          benchmark_pe: item.benchmark_pe,
          date: item.date,
          relative_position: item.relative_position,
          value: this.calculateValuationScore(item.pe, item.benchmark_pe),
        }));
        this.drawChart(gaugeData);
      } else {
        this.drawChart([]);
      }
    } catch (err) {
      console.error("Error setting up chart:", err);
    }
  }

  private handleResize(): void {
    if (this.currentChart && Array.isArray(this.currentChart)) {
      try {
        this.currentChart.forEach((chart) => {
          if (chart) chart.resize();
        });
      } catch (err) {
        console.error("Error resizing chart:", err);
      }
    }
  }

  private updateDisplay(): void {
    if (this.currentChart && Array.isArray(this.currentChart)) {
      this.currentChart.forEach((chart) => {
        if (chart) chart.resize();
      });
    }
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list)
      this.eventHandlers.set(
        type,
        list.filter((h) => h !== handler),
      );
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector(".widgetBox") as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: "white" | "black" | "default-dark" | "default-lite" | "default-lite"): void {
    if (!this.shadowRoot) return;

    this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as
      | "white"
      | "black"
      | "default-dark";

    this.updateDisplay();
  }

  public destroy(): void {
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
      this.widgetResizeObserver = null;
    }

    if (this.currentChart && Array.isArray(this.currentChart)) {
      this.currentChart.forEach((chart) => {
        if (chart) chart.dispose();
      });
      this.currentChart = [];
    }

    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = "";
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const evt: WidgetEvent = { type, data };
      handlers.forEach((h) => h(evt));
    }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
  }

}

declare global {
  interface Window {
    TraderverseValuationGaugeDashboard: typeof TraderverseValuationGaugeDashboard;
  }
}

if (typeof window !== "undefined") {
  window.TraderverseValuationGaugeDashboard =
    TraderverseValuationGaugeDashboard;
}